import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { BASELINE } from '../../util';
import { UserContext } from '../../context/UserContext';
import { toast } from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const A4_W = 794;
const A4_H = 1123;
const PAGE_PAD_V = 56;
const PAGE_PAD_H = 64;
const DOC_HEADER_H = 130;
const ROW_GAP = 20;

const uid = () => Math.random().toString(36).slice(2, 9);

const SUBJECT_CATEGORIES = {
    Math: ['Algebra', 'Advanced Math', 'Problem-Solving and Data Analysis', 'Geometry and Trigonometry'],
    Reading: ['Information and Ideas', 'Craft and Structure', 'Expression of Ideas', 'Standard English Conventions'],
};
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];

const stripHtml = (html = '') => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
};

const estimateQHeight = (q) => {
    if (!q) return 90;
    let h = 28;
    if (q.stimulus) h += Math.min(200, Math.ceil(q.stimulus.replace(/<[^>]*>/g, ' ').length / 3));
    if (q.stem) h += Math.min(80, Math.ceil(q.stem.replace(/<[^>]*>/g, ' ').length / 4) + 20);
    if (q.a != null && q.a !== 'null') h += 88;
    else h += 28;
    return h + 24;
};

// Heavy question = needs its own full-width row
const isHeavyQuestion = (q) => {
    if (!q) return false;
    const stimLen = q.stimulus ? q.stimulus.replace(/<[^>]*>/g, ' ').trim().length : 0;
    const stemLen = q.stem ? q.stem.replace(/<[^>]*>/g, ' ').trim().length : 0;
    // Has a passage/stimulus, or very long stem
    return stimLen > 120 || stemLen > 280;
};

// Returns true if q contains any table — always force full-width row
const hasLargeTable = (q) => {
    if (!q) return false;
    const check = (html) => {
        if (!html) return false;
        if (html.includes('<table')) return true;
        if (/\\begin\{(?:tabular|array)\}/.test(html)) return true;
        return false;
    };
    return check(q.stimulus) || check(q.stem);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function WorksheetPDF() {
    const { classid, quizid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(UserContext);

    // ── Config state ─────────────────────────────────────────────────────────
    const [config, setConfig] = useState({
        title: 'Worksheet',
        teacher: '',
        date: new Date().toLocaleDateString('en-US'),
        subtitle: '',
        showName: true,
        showDate: true,
        showScore: true,
        fontSize: 12,
    });
    const setField = (k) => (v) => setConfig(c => ({ ...c, [k]: v }));

    // ── Question data ─────────────────────────────────────────────────────────
    const [questionMap, setQuestionMap] = useState({});
    const [poolIds, setPoolIds] = useState([]);
    const [loading, setLoading] = useState(true);
    // Per-question settings: { [qid]: { choiceLayout: 'auto'|'1col'|'2x2' } }
    const [questionSettings, setQuestionSettings] = useState({});
    const setQSetting = (qid, key, val) =>
        setQuestionSettings(prev => ({ ...prev, [qid]: { ...(prev[qid] || {}), [key]: val } }));

    // ── Canvas layout ─────────────────────────────────────────────────────────
    // rows: [{ id, cols: 1|2, slots: [rawId|null, rawId|null] }]
    const [rows, setRows] = useState([{ id: uid(), cols: 2, slots: [null, null] }]);

    // Build a deterministic default layout from qids + questionMap
    const buildDefaultLayout = (qids, map) => {
        const newRows = [];
        const newSettings = {};
        let i = 0;
        while (i < qids.length) {
            const qid = String(qids[i]);
            const q = map[qid];
            const forceFullWidth = hasLargeTable(q) || isHeavyQuestion(q);

            if (forceFullWidth) {
                newRows.push({ id: uid(), cols: 1, slots: [qid, null] });
                newSettings[qid] = { choiceLayout: '2x2' };
                i++;
            } else {
                const nextQid = qids[i + 1] ? String(qids[i + 1]) : null;
                const nextQ = nextQid ? map[nextQid] : null;
                const nextHeavy = nextQ && (hasLargeTable(nextQ) || isHeavyQuestion(nextQ));

                if (nextQid && !nextHeavy) {
                    newRows.push({ id: uid(), cols: 2, slots: [qid, nextQid] });
                    newSettings[qid] = { choiceLayout: '1col' };
                    newSettings[nextQid] = { choiceLayout: '1col' };
                    i += 2;
                } else {
                    newRows.push({ id: uid(), cols: 1, slots: [qid, null] });
                    newSettings[qid] = { choiceLayout: '2x2' };
                    i++;
                }
            }
        }
        if (newRows.length === 0) newRows.push({ id: uid(), cols: 2, slots: [null, null] });
        return { rows: newRows, settings: newSettings };
    };

    // ── Pool source ───────────────────────────────────────────────────────────
    const [poolSource, setPoolSource] = useState('test');
    const [dbFilter, setDbFilter] = useState({
        subject: '',
        categories: [],      // empty = all categories for subject
        skills: [],          // empty = all skills for selected categories
        difficulties: [],    // empty = all difficulties
        limit: '40',
    });
    const [dbLoading, setDbLoading] = useState(false);
    const [detectedSubject, setDetectedSubject] = useState(''); // 'Math' | 'Reading' | ''
    const [filterOpen, setFilterOpen] = useState({ subject: true, category: true, difficulty: true });
    const toggleFilter = (k) => setFilterOpen(p => ({ ...p, [k]: !p[k] }));

    // Sub-category (skill_descs) data: { [category]: string[] }
    const [subCategoryMap, setSubCategoryMap] = useState({});
    const [subCatLoading, setSubCatLoading] = useState(false);
    // Which category's sub-panel is open (null = closed)
    const [activeCategory, setActiveCategory] = useState(null);
    // Available question count for current filter
    const [poolCount, setPoolCount] = useState(null);
    const poolCountTimer = useRef(null);

    // ── KaTeX auto-render ─────────────────────────────────────────────────────
    const [katexReady, setKatexReady] = useState(!!window.renderMathInElement);
    useEffect(() => {
        if (window.renderMathInElement) { setKatexReady(true); return; }
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js';
        s.integrity = 'sha384-+VBxd3r6XgURycqtZ117nYw44OOcIax56Z4dCRWbxyPt0Koah1uHoK0o4+/RRE05';
        s.crossOrigin = 'anonymous';
        s.defer = true;
        s.onload = () => setKatexReady(true);
        document.body.appendChild(s);
        return () => { if (document.body.contains(s)) document.body.removeChild(s); };
    }, []);

    // ── Quiz lock (released quizzes: canvas read-only, config still editable) ──
    const [quizLocked, setQuizLocked] = useState(false);

    // ── Answer key mode: 'off' | 'inline' | 'rationale' ─────────────────────
    const [answerKey, setAnswerKey] = useState('off');

    // ── Pool search ───────────────────────────────────────────────────────────
    const [poolSearch, setPoolSearch] = useState('');

    // ── Left panel toggle ─────────────────────────────────────────────────────
    const [leftOpen, setLeftOpen] = useState(true);

    // ── DnD ───────────────────────────────────────────────────────────────────
    const dragItem = useRef(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);

    // ── Page break tracking ───────────────────────────────────────────────────
    const [pageBreakBefore, setPageBreakBefore] = useState(new Set());
    const rowDivRefs = useRef({});

    // ─────────────────────────────────────────────────────────────────────────
    // Page break calculation
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const recalcBreaks = () => {
            const contentH = A4_H - PAGE_PAD_V * 2;
            const breaks = new Set();
            let cumH = DOC_HEADER_H;

            rows.forEach((row, i) => {
                const domEl = rowDivRefs.current[row.id];
                let rowH;
                if (domEl) {
                    // Subtract screen-only (.no-print) UI heights — they don't appear in print
                    let noPrintH = 0;
                    domEl.querySelectorAll('.no-print').forEach(el => { noPrintH += el.offsetHeight || 0; });
                    rowH = Math.max(30, domEl.offsetHeight - noPrintH) + ROW_GAP;
                } else {
                    rowH = Math.max(
                        estimateQHeight(questionMap[row.slots[0]]),
                        row.cols === 2 ? estimateQHeight(questionMap[row.slots[1]]) : 0,
                        90
                    ) + ROW_GAP;
                }

                if (cumH + rowH > contentH) {
                    breaks.add(i);
                    cumH = rowH;
                } else {
                    cumH += rowH;
                }
            });

            setPageBreakBefore(breaks);
        };

        recalcBreaks();

        // Re-calculate when row heights change due to image or KaTeX loading
        const observer = new ResizeObserver(recalcBreaks);
        Object.values(rowDivRefs.current).forEach(el => { if (el) observer.observe(el); });

        // Safety net: also catch image loads that may not trigger ResizeObserver
        const canvas = document.querySelector('.a4-outer');
        const pendingImgs = canvas
            ? Array.from(canvas.querySelectorAll('img')).filter(img => !img.complete)
            : [];
        pendingImgs.forEach(img => img.addEventListener('load', recalcBreaks, { once: true }));

        return () => {
            observer.disconnect();
            pendingImgs.forEach(img => img.removeEventListener('load', recalcBreaks));
        };
    }, [rows, questionMap, katexReady]);

    // ─────────────────────────────────────────────────────────────────────────
    // Data loading
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => { initLoad(); }, []);

    // Auto-load DB questions when switching to 'db' tab if subject is known
    const dbAutoLoaded = useRef(false);
    useEffect(() => {
        if (poolSource === 'db' && detectedSubject && !dbAutoLoaded.current) {
            dbAutoLoaded.current = true;
            loadDBQuestions(detectedSubject);
        }
    }, [poolSource, detectedSubject]);

    // Fetch available question count whenever filter changes (debounced 300ms)
    useEffect(() => {
        if (poolSource !== 'db') return;
        clearTimeout(poolCountTimer.current);
        poolCountTimer.current = setTimeout(async () => {
            try {
                const params = {};
                const subject = dbFilter.subject || detectedSubject;
                if (dbFilter.skills.length > 0) {
                    params.skills = dbFilter.skills.join('||');
                } else if (dbFilter.categories.length > 0) {
                    params.categories = dbFilter.categories.join('||');
                } else if (subject) {
                    params.subject = subject;
                }
                if (dbFilter.difficulties.length > 0) params.difficulties = dbFilter.difficulties.join(',');
                const res = await axios.get(BASELINE + 'questions/pool/count', { params });
                setPoolCount(res.data.count ?? null);
            } catch { setPoolCount(null); }
        }, 300);
        return () => clearTimeout(poolCountTimer.current);
    }, [poolSource, dbFilter, detectedSubject]);

    const detectSubjectFromTestMeta = (test) => {
        // 1. Check Setting.mode (section saved per-question during autosave)
        try {
            const setting = test.Setting ? JSON.parse(test.Setting) : null;
            if (setting?.mode) {
                const m = setting.mode.toLowerCase();
                if (m.includes('math')) return 'Math';
                if (m.includes('read') || m.includes('writ') || m.includes('eng')) return 'Reading';
            }
        } catch {}
        // 2. Check test name
        const name = (test.name || '').toLowerCase();
        if (name.includes('math')) return 'Math';
        if (name.includes('read') || name.includes('writ') || name.includes('eng')) return 'Reading';
        return '';
    };

    const initLoad = async () => {
        setLoading(true);
        try {
            let test = location.state?.test;
            if (!test && user) {
                const r = await axios.get(BASELINE + 'getautosave', { params: { id: user.u_id } });
                test = (r.data?.data || []).find(t => t.testid === quizid) ?? null;
            }
            if (!test) { toast.error('Test not found'); return; }

            // Lock canvas if quiz is released
            if (test.status === 'released') setQuizLocked(true);

            // Lock subject from test metadata first (fastest, no extra fetch)
            const metaSubject = detectSubjectFromTestMeta(test);
            if (metaSubject) {
                setDetectedSubject(metaSubject);
                setDbFilter(f => ({ ...f, subject: metaSubject }));
            }

            const qids = JSON.parse(test.quiz_data)?.questions ?? [];
            const newTitle = test.name || '';
            if (newTitle) setField('title')(newTitle);

            if (qids.length > 0) {
                const map = await fetchSATQuestions(qids, metaSubject);

                if (test.pdf_layout) {
                    // Restore saved PDF layout
                    try {
                        const saved = JSON.parse(test.pdf_layout);
                        if (Array.isArray(saved.rows) && saved.rows.length > 0) setRows(saved.rows);
                        if (saved.questionSettings) setQuestionSettings(saved.questionSettings);
                        if (saved.config) setConfig(c => ({ ...c, ...saved.config }));
                    } catch (e) {
                        console.error('Failed to parse pdf_layout:', e);
                    }
                } else {
                    // First load — auto-generate layout and save immediately
                    const { rows: newRows, settings: newSettings } = buildDefaultLayout(qids, map);
                    setRows(newRows);
                    setQuestionSettings(newSettings);

                    const orderedIds = newRows.flatMap(r => r.slots.filter(Boolean));
                    const baseConfig = { title: newTitle || 'Worksheet', teacher: '', date: new Date().toLocaleDateString('en-US'), subtitle: '', showName: true, showDate: true, showScore: true, fontSize: 12 };
                    try {
                        await axios.post(BASELINE + 'worksheet/sync', {
                            testid: quizid,
                            userid: user.u_id,
                            questions: orderedIds,
                            pdf_layout: JSON.stringify({ rows: newRows, questionSettings: newSettings, config: baseConfig }),
                        });
                    } catch (e) {
                        console.error('Auto-save pdf_layout failed:', e);
                    }
                }
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to load test data');
        } finally {
            setLoading(false);
        }
    };

    const fetchSATQuestions = async (qids, knownSubject = '') => {
        const res = await axios.get(BASELINE + 'questions/sat', { params: { id: qids } });
        const flat = Array.isArray(res.data[0]) ? res.data[0] : Array.isArray(res.data) ? res.data : [];
        const map = {};
        qids.forEach((id, i) => {
            const q = flat[i];
            if (q) map[String(id)] = q;
        });
        setQuestionMap(map);
        setPoolIds(qids.map(String));

        // Only override if we didn't already lock from test meta
        if (!knownSubject) {
            const subject = detectSubjectFromQuestions(Object.values(map));
            if (subject) {
                setDetectedSubject(subject);
                setDbFilter(f => ({ ...f, subject }));
            }
        }
        return map;
    };

    const detectSubjectFromQuestions = (questions) => {
        if (!questions.length) return '';

        const MATH_CATS = ['algebra', 'advanced math', 'problem-solving', 'data analysis', 'geometry', 'trigonometry'];
        const READ_CATS = ['information and ideas', 'craft and structure', 'expression of ideas', 'standard english conventions'];

        let mathScore = 0, engScore = 0;
        questions.forEach(q => {
            // primary_class_cd_descs is the most reliable field (e.g. "Algebra", "Information and Ideas")
            const cat = (q.primary_class_cd_descs || q.skill_descs || q.skill || q.topic || '').toLowerCase();
            if (MATH_CATS.some(k => cat.includes(k))) mathScore++;
            if (READ_CATS.some(k => cat.includes(k))) engScore++;
        });

        if (mathScore === 0 && engScore === 0) return '';
        return mathScore >= engScore ? 'Math' : 'Reading';
    };

    const fetchSubCategories = async (category) => {
        if (subCategoryMap[category]) {
            // already loaded — just open the panel
            setActiveCategory(prev => prev === category ? null : category);
            return;
        }
        setSubCatLoading(true);
        try {
            const res = await axios.get(BASELINE + 'questions/skills', {
                params: { categories: category },
            });
            setSubCategoryMap(prev => ({ ...prev, ...res.data }));
            setActiveCategory(category);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load sub-categories');
        } finally {
            setSubCatLoading(false);
        }
    };

    // subjectOverride: used when calling from auto-load effect before state settles
    const loadDBQuestions = async (subjectOverride) => {
        setDbLoading(true);
        try {
            const params = {};
            const subject = subjectOverride ?? dbFilter.subject;
            // Skills (sub-categories) take highest priority
            if (dbFilter.skills.length > 0) {
                params.skills = dbFilter.skills.join('||');
            } else if (dbFilter.categories.length > 0) {
                params.categories = dbFilter.categories.join('||');
            } else if (subject) {
                params.subject = subject;
            }
            if (dbFilter.difficulties.length > 0) params.difficulties = dbFilter.difficulties.join(',');
            params.limit = dbFilter.limit || 40;
            const res = await axios.get(BASELINE + 'questions/pool', { params });
            const arr = Array.isArray(res.data) ? res.data : [];
            const map = {};
            arr.forEach((q, i) => {
                const id = q.questionIds ? String(q.questionIds) : `db-${i}`;
                map[id] = q;
            });
            setQuestionMap(prev => ({ ...prev, ...map }));
            setPoolIds(Object.keys(map));
            toast.success(`${arr.length} questions loaded from DB`);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load DB questions');
        } finally {
            setDbLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Sync to Quiz
    // ─────────────────────────────────────────────────────────────────────────
    const [syncing, setSyncing] = useState(false);

    const syncToQuiz = async () => {
        // Extract ordered qids from canvas: top→bottom, left→right, skip nulls
        const orderedIds = [];
        rows.forEach(r => r.slots.forEach(id => { if (id) orderedIds.push(id); }));

        if (orderedIds.length === 0) {
            toast.error('No questions on canvas to sync');
            return;
        }

        setSyncing(true);
        try {
            await axios.post(BASELINE + 'worksheet/sync', {
                testid: quizid,
                userid: user.u_id,
                questions: orderedIds,
                pdf_layout: JSON.stringify({ rows, questionSettings, config }),
            });
            toast.success(`Saved ${orderedIds.length} questions to quiz`);
        } catch (e) {
            console.error(e);
            toast.error('Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Canvas helpers
    // ─────────────────────────────────────────────────────────────────────────
    const usedQids = useCallback(() => {
        const s = new Set();
        rows.forEach(r => r.slots.forEach(id => { if (id) s.add(id); }));
        return s;
    }, [rows]);

    const getQNum = (rowIdx, slotIdx) => {
        let n = 0;
        for (let i = 0; i < rowIdx; i++) {
            const r = rows[i];
            if (r.slots[0]) n++;
            if (r.cols === 2 && r.slots[1]) n++;
        }
        if (slotIdx > 0) {
            if (rows[rowIdx]?.slots[0]) n++;
        }
        return n + 1;
    };

    const addRow = (afterId = null, cols = 2) => {
        const nr = { id: uid(), cols, slots: [null, null] };
        setRows(prev => {
            if (!afterId) return [...prev, nr];
            const idx = prev.findIndex(r => r.id === afterId);
            const next = [...prev];
            next.splice(idx + 1, 0, nr);
            return next;
        });
    };

    const addRowBefore = (beforeId, cols = 2) => {
        const nr = { id: uid(), cols, slots: [null, null] };
        setRows(prev => {
            const idx = prev.findIndex(r => r.id === beforeId);
            if (idx < 0) return [...prev, nr];
            const next = [...prev];
            next.splice(idx, 0, nr);
            return next;
        });
    };

    const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

    const toggleRowCols = (rowId) => {
        setRows(prev => prev.flatMap(r => {
            if (r.id !== rowId) return [r];
            if (r.cols === 2) {
                // Split: if both slots filled → two separate 1Q rows; otherwise just shrink
                if (r.slots[0] && r.slots[1]) {
                    return [
                        { id: r.id, cols: 1, slots: [r.slots[0], null] },
                        { id: uid(), cols: 1, slots: [r.slots[1], null] },
                    ];
                }
                return [{ ...r, cols: 1, slots: [r.slots[0], null] }];
            } else {
                return [{ ...r, cols: 2 }];
            }
        }));
    };

    const clearSlot = (rowId, slotIdx) =>
        setRows(prev => prev.map(r =>
            r.id !== rowId ? r : { ...r, slots: r.slots.map((s, i) => i === slotIdx ? null : s) }
        ));

    const handleRandom = () => {
        const ids = [...poolIds].sort(() => Math.random() - 0.5);
        const newRows = [];
        const newSettings = {};
        let i = 0;
        while (i < ids.length) {
            const qid = ids[i];
            const q = questionMap[qid];

            // Large-table questions always get a full-width row
            if (hasLargeTable(q)) {
                newRows.push({ id: uid(), cols: 1, slots: [qid, null] });
                newSettings[qid] = { choiceLayout: '2x2' };
                i++;
                continue;
            }

            // Don't pair with the next question if it also has a large table
            const canPair = i + 1 < ids.length && !hasLargeTable(questionMap[ids[i + 1]]);
            const use2Q = canPair && Math.random() < 0.5;

            if (use2Q) {
                newRows.push({ id: uid(), cols: 2, slots: [ids[i], ids[i + 1]] });
                newSettings[ids[i]]     = { choiceLayout: '1col' };
                newSettings[ids[i + 1]] = { choiceLayout: '1col' };
                i += 2;
            } else {
                newRows.push({ id: uid(), cols: 1, slots: [ids[i], null] });
                newSettings[ids[i]] = { choiceLayout: '2x2' };
                i++;
            }
        }
        if (newRows.length === 0) newRows.push({ id: uid(), cols: 2, slots: [null, null] });
        setRows(newRows);
        setQuestionSettings(prev => ({ ...prev, ...newSettings }));
        toast.success(`${ids.length} questions arranged randomly`);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // DnD handlers
    // ─────────────────────────────────────────────────────────────────────────
    const onDragStart = (e, item) => {
        dragItem.current = item;
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e, target) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverTarget(target);
    };

    const onDragLeave = () => setDragOverTarget(null);

    const onDrop = (e, target) => {
        e.preventDefault();
        setDragOverTarget(null);
        const src = dragItem.current;
        if (!src) return;
        dragItem.current = null;

        if (target.type === 'slot') {
            const { rowId, slotIdx } = target;
            const destRow = rows.find(r => r.id === rowId);
            const needsFullWidth = hasLargeTable(questionMap[src.qid]);

            setRows(prev => {
                const dr = prev.find(r => r.id === rowId);
                if (!dr) return prev;
                const destQid = dr.slots[slotIdx];

                // Place the question in the target slot
                let updated = prev.map(r => {
                    const slots = [...r.slots];
                    if (src.source === 'canvas' && r.id === src.rowId) {
                        slots[src.slotIdx] = destQid ?? null;
                    }
                    if (r.id === rowId) {
                        slots[slotIdx] = src.qid;
                    }
                    return { ...r, slots };
                });

                // Large-table question landed in a 2-col row → split to 1-col
                if (needsFullWidth) {
                    updated = updated.flatMap(r => {
                        if (r.id !== rowId || r.cols === 1) return [r];
                        if (r.slots[0] && r.slots[1]) {
                            return [
                                { id: r.id, cols: 1, slots: [r.slots[0], null] },
                                { id: uid(), cols: 1, slots: [r.slots[1], null] },
                            ];
                        }
                        return [{ ...r, cols: 1, slots: [r.slots[0], null] }];
                    });
                }

                return updated;
            });

            // Apply default choice layout when dropping from pool into a slot
            if (src.source === 'pool' && destRow) {
                const defaultLayout = needsFullWidth || destRow.cols === 1 ? '2x2' : '1col';
                setQuestionSettings(prev => {
                    // Only set if no existing manual setting
                    if (prev[src.qid]?.choiceLayout) return prev;
                    return { ...prev, [src.qid]: { ...(prev[src.qid] || {}), choiceLayout: defaultLayout } };
                });
            }
        } else if (target.type === 'pool' && src.source === 'canvas') {
            setRows(prev => prev.map(r =>
                r.id !== src.rowId ? r :
                { ...r, slots: r.slots.map((s, i) => i === src.slotIdx ? null : s) }
            ));
        }
    };

    const onDragEnd = () => { dragItem.current = null; setDragOverTarget(null); };

    const handlePrint = () => window.print();

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    if (loading) return <Spinner />;

    const used = usedQids();

    return (
        <>
            <PrintStyles fontSize={config.fontSize} />
            <div className="ws-root" style={S.root}>

                {/* ═══════════════════════════════════════════════════════════
                    LEFT PANEL
                ═══════════════════════════════════════════════════════════ */}
                <aside className="no-print" style={{ ...S.left, ...(leftOpen ? {} : { width: 0, minWidth: 0, overflow: 'hidden', borderRight: 'none' }) }}>
                    <div style={S.leftTopBar}>
                        <button onClick={() => navigate(-1)} style={S.backBtn}>← Back</button>
                        <button onClick={() => setLeftOpen(false)} style={S.collapseBtn} title="Hide panel">‹ Hide</button>
                        <button
                            onClick={syncToQuiz}
                            disabled={syncing}
                            style={{
                                ...S.printBtn,
                                background: syncing
                                    ? 'linear-gradient(135deg,#3730a3,#4338ca)'
                                    : 'linear-gradient(135deg,#6366f1,#818cf8)',
                                boxShadow: '0 3px 14px rgba(99,102,241,0.35)',
                                opacity: syncing ? 0.75 : 1,
                                cursor: syncing ? 'not-allowed' : 'pointer',
                            }}
                            title="Save question order to quiz"
                        >{syncing ? 'Saving…' : 'Save'}</button>
                        <button
                            onClick={() => setAnswerKey(v => v === 'off' ? 'inline' : v === 'inline' ? 'rationale' : 'off')}
                            style={{
                                ...S.printBtn,
                                background: answerKey === 'rationale'
                                    ? 'linear-gradient(135deg,#5b21b6,#7c3aed)'
                                    : answerKey === 'inline'
                                    ? 'linear-gradient(135deg,#065f46,#059669)'
                                    : 'linear-gradient(135deg,#1f2937,#374151)',
                                boxShadow: answerKey === 'rationale'
                                    ? '0 2px 8px rgba(124,58,237,0.45)'
                                    : answerKey === 'inline'
                                    ? '0 2px 8px rgba(5,150,105,0.45)'
                                    : '0 2px 8px rgba(0,0,0,0.3)',
                                border: answerKey === 'rationale'
                                    ? '1px solid rgba(196,181,253,0.5)'
                                    : answerKey === 'inline'
                                    ? '1px solid rgba(52,211,153,0.5)'
                                    : '1px solid rgba(255,255,255,0.12)',
                                transition: 'all 0.18s ease',
                                gap: 6,
                            }}
                            title="Cycle answer key mode: Off → Inline → Rationale"
                        >
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                background: answerKey === 'rationale' ? '#a78bfa' : answerKey === 'inline' ? '#34d399' : 'rgba(255,255,255,0.25)',
                                boxShadow: answerKey === 'rationale' ? '0 0 6px #a78bfa' : answerKey === 'inline' ? '0 0 6px #34d399' : 'none',
                                transition: 'all 0.18s ease',
                            }} />
                            {answerKey === 'rationale' ? 'Key: Rationale' : answerKey === 'inline' ? 'Key: Inline' : 'Answer Key'}
                        </button>
                        <button onClick={handlePrint} style={S.printBtn}>Print</button>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                        {/* ── Worksheet Settings ── */}
                        <Section label="Worksheet Info">
                            <LabeledInput label="Title" value={config.title} onChange={setField('title')} />
                            <LabeledInput label="Teacher" value={config.teacher} onChange={setField('teacher')} />
                            <LabeledInput label="Date" value={config.date} onChange={setField('date')} />
                            <LabeledInput label="Subtitle" value={config.subtitle} onChange={setField('subtitle')} placeholder="(optional)" />
                            {/* Font size control */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, width: 64, flexShrink: 0 }}>Font</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                                    <button
                                        onClick={() => setField('fontSize')(Math.max(7, config.fontSize - 0.5))}
                                        style={S.fontStepBtn}
                                    >−</button>
                                    <span style={{ color: 'white', fontSize: 14, minWidth: 42, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                        {config.fontSize}pt
                                    </span>
                                    <button
                                        onClick={() => setField('fontSize')(Math.min(16, config.fontSize + 0.5))}
                                        style={S.fontStepBtn}
                                    >+</button>
                                    <input
                                        type="range" min="7" max="16" step="0.5"
                                        value={config.fontSize}
                                        onChange={e => setField('fontSize')(parseFloat(e.target.value))}
                                        style={{ flex: 1, accentColor: '#6366f1', cursor: 'pointer' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: 6 }}>
                                <p style={S.microLabel}>Student Fields</p>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                                    {[['showName', 'Name'], ['showDate', 'Date'], ['showScore', 'Score']].map(([k, l]) => (
                                        <ToggleChip key={k} active={config[k]} onClick={() => setField(k)(!config[k])} label={l} />
                                    ))}
                                </div>
                            </div>
                        </Section>

                        {/* ── Source ── */}
                        {quizLocked && (
                            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(139,92,246,0.20)', textAlign: 'center' }}>
                                <p style={{ color: 'rgba(196,181,253,0.70)', fontSize: 12, margin: 0 }}>🔒 Question pool disabled for released quizzes</p>
                            </div>
                        )}
                        {!quizLocked && <Section label="Question Pool">
                            <div style={{ display: 'flex', gap: 8, marginBottom: poolSource === 'db' ? 0 : 4 }}>
                                {[['test', 'This Test'], ['db', 'Search DB']].map(([v, l]) => (
                                    <button key={v} style={{ ...S.srcBtn, ...(poolSource === v ? S.srcBtnActive : {}) }}
                                        onClick={() => setPoolSource(v)}>{l}</button>
                                ))}
                            </div>

                            {poolSource === 'db' && (
                                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 0, marginLeft: -18, marginRight: -18, borderTop: '1px solid rgba(255,255,255,0.07)' }}>

                                    {/* ── Subject accordion ── */}
                                    <FilterAccordion
                                        label="Subject"
                                        open={filterOpen.subject}
                                        onToggle={() => toggleFilter('subject')}
                                    >
                                        {detectedSubject ? (
                                            <div style={S.lockedSubjectBadge}>
                                                <span style={{ fontSize: 11, opacity: 0.6 }}>🔒 Locked:</span>
                                                <span style={{ fontWeight: 700, fontSize: 13 }}>
                                                    {detectedSubject === 'Math' ? '📐 Math' : '📖 Reading & Writing'}
                                                </span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {['Math', 'Reading'].map(subj => (
                                                    <FilterCheckRow
                                                        key={subj}
                                                        label={subj === 'Math' ? '📐 Math' : '📖 Reading & Writing'}
                                                        checked={dbFilter.subject === subj}
                                                        radio
                                                        onChange={() => setDbFilter(f => ({
                                                            ...f,
                                                            subject: f.subject === subj ? '' : subj,
                                                            categories: [],
                                                        }))}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </FilterAccordion>

                                    {/* ── Category accordion ── */}
                                    {(detectedSubject || dbFilter.subject) && (
                                        <FilterAccordion
                                            label="Category"
                                            open={filterOpen.category}
                                            onToggle={() => toggleFilter('category')}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                {(SUBJECT_CATEGORIES[detectedSubject || dbFilter.subject] || []).map(cat => {
                                                    const catChecked = dbFilter.categories.length === 0 || dbFilter.categories.includes(cat);
                                                    const isActive = activeCategory === cat;
                                                    const loadedSkills = subCategoryMap[cat] || [];
                                                    // skills selected for this category (only computable when sub-cats loaded)
                                                    const selSkills = loadedSkills.length > 0
                                                        ? (dbFilter.skills.length === 0
                                                            ? loadedSkills          // "all" means all are selected
                                                            : dbFilter.skills.filter(s => loadedSkills.includes(s)))
                                                        : [];
                                                    const partialSkillSel = dbFilter.skills.length > 0 && loadedSkills.length > 0 && selSkills.length < loadedSkills.length;
                                                    return (
                                                        <div key={cat}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                {/* Checkbox — toggles this category in/out */}
                                                                <FilterCheckRow
                                                                    label={cat}
                                                                    checked={catChecked}
                                                                    onChange={() => {
                                                                        const allCats = SUBJECT_CATEGORIES[detectedSubject || dbFilter.subject] || [];
                                                                        const catSkills = subCategoryMap[cat] || [];
                                                                        setDbFilter(f => {
                                                                            const currentCats = f.categories.length === 0 ? [...allCats] : [...f.categories];
                                                                            const removing = currentCats.includes(cat);
                                                                            const nextCats = removing
                                                                                ? currentCats.filter(c => c !== cat)
                                                                                : [...currentCats, cat];

                                                                            let nextSkills = f.skills;
                                                                            if (removing && catSkills.length > 0) {
                                                                                // Expand "all" to explicit list, then remove this category's skills
                                                                                const base = f.skills.length === 0
                                                                                    ? Object.values(subCategoryMap).flat()
                                                                                    : f.skills;
                                                                                nextSkills = base.filter(s => !catSkills.includes(s));
                                                                            } else if (!removing && catSkills.length > 0 && f.skills.length > 0) {
                                                                                // Re-adding category — restore its skills
                                                                                const restored = [...new Set([...f.skills, ...catSkills])];
                                                                                // If all skills from all selected cats are now included → reset to "all"
                                                                                const allKnown = Object.values(subCategoryMap).flat();
                                                                                nextSkills = allKnown.every(s => restored.includes(s)) ? [] : restored;
                                                                            }

                                                                            return {
                                                                                ...f,
                                                                                categories: nextCats.length === allCats.length ? [] : nextCats,
                                                                                skills: nextSkills,
                                                                            };
                                                                        });
                                                                    }}
                                                                    style={{ flex: 1 }}
                                                                />
                                                                {/* → button — opens sub-category panel */}
                                                                <button
                                                                    onClick={() => fetchSubCategories(cat)}
                                                                    style={{ ...S.subCatBtn, background: isActive ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)', color: isActive ? '#a5b4fc' : 'rgba(255,255,255,0.4)' }}
                                                                    title="View sub-categories"
                                                                >
                                                                    {subCatLoading && isActive ? '…' : isActive ? '›' : '›'}
                                                                </button>
                                                            </div>
                                                            {/* Skill count badge — only when some (not all) skills of this cat are selected */}
                                                            {partialSkillSel && (
                                                                <div style={{ paddingLeft: 25, marginTop: 2 }}>
                                                                    <span style={{ fontSize: 10, color: '#a5b4fc', background: 'rgba(99,102,241,0.15)', borderRadius: 4, padding: '1px 6px' }}>
                                                                        {selSkills.length}/{loadedSkills.length} skills
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </FilterAccordion>
                                    )}

                                    {/* ── Difficulty accordion ── */}
                                    <FilterAccordion
                                        label="Difficulty"
                                        open={filterOpen.difficulty}
                                        onToggle={() => toggleFilter('difficulty')}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                                            {DIFFICULTIES.map(d => {
                                                const checked = dbFilter.difficulties.length === 0 || dbFilter.difficulties.includes(d);
                                                const color = d === 'Easy' ? '#22c55e' : d === 'Medium' ? '#f59e0b' : '#ef4444';
                                                return (
                                                    <button
                                                        key={d}
                                                        onClick={() => setDbFilter(f => {
                                                            const current = f.difficulties.length === 0 ? [...DIFFICULTIES] : [...f.difficulties];
                                                            const next = current.includes(d)
                                                                ? current.filter(x => x !== d)
                                                                : [...current, d];
                                                            return { ...f, difficulties: next.length === DIFFICULTIES.length ? [] : next };
                                                        })}
                                                        style={{
                                                            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${color}`,
                                                            background: checked ? color + '22' : 'transparent',
                                                            color: checked ? color : 'rgba(255,255,255,0.4)',
                                                            transition: 'all 0.15s',
                                                        }}
                                                    >{d}</button>
                                                );
                                            })}
                                        </div>
                                    </FilterAccordion>

                                    {/* ── Limit + Load ── */}
                                    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {/* Max available count */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: 'sans-serif' }}>Available</span>
                                            <span
                                                onClick={() => poolCount > 0 && setDbFilter(f => ({ ...f, limit: String(poolCount) }))}
                                                style={{
                                                    fontSize: 13, fontWeight: 700, fontFamily: 'sans-serif',
                                                    color: poolCount === null ? 'rgba(255,255,255,0.25)' : poolCount === 0 ? '#f87171' : '#34d399',
                                                    cursor: poolCount > 0 ? 'pointer' : 'default',
                                                    textDecoration: poolCount > 0 ? 'underline dotted' : 'none',
                                                    textUnderlineOffset: 3,
                                                }}
                                                title={poolCount > 0 ? `Set limit to ${poolCount}` : undefined}
                                            >
                                                {poolCount === null ? '—' : poolCount.toLocaleString()} questions
                                            </span>
                                        </div>
                                        {/* Limit row */}
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, flexShrink: 0 }}>Limit</span>
                                            <input type="number" min="5" max={poolCount || 200} value={dbFilter.limit}
                                                onChange={e => setDbFilter(f => ({ ...f, limit: e.target.value }))}
                                                style={{ ...S.input, width: 70, flexShrink: 0 }} />
                                            {poolCount !== null && (
                                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'sans-serif' }}>
                                                    / {poolCount.toLocaleString()}
                                                </span>
                                            )}
                                            <button onClick={() => loadDBQuestions()} disabled={dbLoading}
                                                style={{ ...S.loadBtn, flex: 1, opacity: dbLoading ? 0.6 : 1 }}>
                                                {dbLoading ? 'Loading…' : 'Load'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button onClick={handleRandom} style={{ ...S.randomBtn, marginTop: poolSource === 'db' ? 4 : 0 }} disabled={poolIds.length === 0}>
                                🎲 Random Arrange  ({poolIds.length} Qs)
                            </button>
                        </Section>}

                        {/* ── Pool List ── */}
                        {!quizLocked && (() => {
                            const needle = poolSearch.trim().toLowerCase();
                            const filteredIds = needle
                                ? poolIds.filter(qid => {
                                    const q = questionMap[qid];
                                    if (!q) return true;
                                    const hay = [q.stem, q.skill, q.topic, q.primary_class_cd_descs, q.skill_descs]
                                        .map(v => stripHtml(v || '').toLowerCase()).join(' ');
                                    return hay.includes(needle);
                                })
                                : poolIds;
                            return (
                                <div
                                    style={{
                                        ...S.poolDropZone,
                                        ...(dragOverTarget?.type === 'pool' ? S.poolDropZoneOver : {}),
                                    }}
                                    onDragOver={e => onDragOver(e, { type: 'pool' })}
                                    onDragLeave={onDragLeave}
                                    onDrop={e => onDrop(e, { type: 'pool' })}
                                >
                                    <p style={{ ...S.microLabel, marginBottom: 8 }}>
                                        {needle
                                            ? `${filteredIds.length} / ${poolIds.length} questions`
                                            : `${poolIds.length} questions`}
                                        {'  ·  '}{used.size} placed
                                    </p>
                                    {/* Search bar */}
                                    <div style={{ position: 'relative', marginBottom: 10 }}>
                                        <input
                                            type="text"
                                            placeholder="Search questions…"
                                            value={poolSearch}
                                            onChange={e => setPoolSearch(e.target.value)}
                                            style={{
                                                width: '100%', boxSizing: 'border-box',
                                                background: 'white',
                                                border: '1px solid #d1d5db',
                                                borderRadius: 7,
                                                color: '#111827',
                                                fontSize: 13,
                                                padding: '7px 32px 7px 10px',
                                                outline: 'none',
                                            }}
                                        />
                                        {poolSearch && (
                                            <button
                                                onClick={() => setPoolSearch('')}
                                                style={{
                                                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                                    background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                                                    cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0,
                                                }}
                                            >✕</button>
                                        )}
                                    </div>
                                    {poolIds.length === 0 && (
                                        <p style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 0' }}>
                                            No questions loaded
                                        </p>
                                    )}
                                    {needle && filteredIds.length === 0 && poolIds.length > 0 && (
                                        <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                                            No matching questions
                                        </p>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {filteredIds.map((qid, idx) => {
                                            const q = questionMap[qid];
                                            const isUsed = used.has(qid);
                                            return (
                                                <PoolCard
                                                    key={qid}
                                                    qid={qid}
                                                    idx={idx}
                                                    q={q}
                                                    isUsed={isUsed}
                                                    onDragStart={onDragStart}
                                                    onDragEnd={onDragEnd}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </aside>


                {/* ═══════════════════════════════════════════════════════════
                    SUB-CATEGORY PANEL (floating over canvas)
                ═══════════════════════════════════════════════════════════ */}
                {activeCategory && (
                    <SubCategoryPanel
                        category={activeCategory}
                        skills={subCategoryMap[activeCategory] || []}
                        selectedSkills={dbFilter.skills}
                        loading={subCatLoading}
                        onClose={() => setActiveCategory(null)}
                        onToggleSkill={(skill) => setDbFilter(f => {
                            const allKnown = Object.values(subCategoryMap).flat();
                            const current = f.skills.length === 0 ? [...allKnown] : [...f.skills];
                            const next = current.includes(skill)
                                ? current.filter(s => s !== skill)
                                : [...current, skill];
                            // if all known skills are selected again → reset to [] (all)
                            const allSelected = allKnown.length > 0 && allKnown.every(s => next.includes(s));
                            return { ...f, skills: allSelected ? [] : next };
                        })}
                        onSelectAll={() => setDbFilter(f => {
                            const catSkills = subCategoryMap[activeCategory] || [];
                            if (f.skills.length === 0 || catSkills.length === 0) return f; // already all
                            const next = [...new Set([...f.skills, ...catSkills])];
                            const allKnown = Object.values(subCategoryMap).flat();
                            return { ...f, skills: allKnown.every(s => next.includes(s)) ? [] : next };
                        })}
                        onClearAll={() => setDbFilter(f => {
                            const catSkills = subCategoryMap[activeCategory] || [];
                            const base = f.skills.length === 0
                                ? Object.values(subCategoryMap).flat()
                                : f.skills;
                            return { ...f, skills: base.filter(s => !catSkills.includes(s)) };
                        })}
                    />
                )}

                {/* ═══════════════════════════════════════════════════════════
                    RIGHT PANEL — A4 Canvas
                ═══════════════════════════════════════════════════════════ */}
                <main className="ws-right" style={S.right}>
                    {/* Panel open button — only visible when left panel is collapsed */}
                    {!leftOpen && (
                        <button
                            className="no-print"
                            onClick={() => setLeftOpen(true)}
                            style={S.openPanelBtn}
                            title="Open panel"
                        >
                            ☰
                        </button>
                    )}
                    {/* Lock banner */}
                    {quizLocked && (
                        <div className="no-print" style={{ margin: '12px 16px 0', padding: '10px 16px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(139,92,246,0.32)' }}>
                            <span style={{ fontSize: 16 }}>🔒</span>
                            <div>
                                <p style={{ color: '#c4b5fd', fontSize: 13, fontWeight: 700, margin: 0 }}>Released — Questions Locked</p>
                                <p style={{ color: 'rgba(196,181,253,0.60)', fontSize: 11, margin: '2px 0 0' }}>Assigned to a class. Questions cannot be modified. Config and print settings are still editable.</p>
                            </div>
                        </div>
                    )}
                    <div className="print-canvas-scroll" style={S.canvasScroll}>
                        {answerKey === 'rationale' ? (
                            <RationaleSheet
                                rows={rows}
                                questionMap={questionMap}
                                config={config}
                                getQNum={getQNum}
                                katexReady={katexReady}
                            />
                        ) : (
                        <div className="a4-outer" style={S.a4Outer}>

                            <DocHeader config={config} />

                            {(() => {
                                let pageNum = 1;
                                return rows.map((row, rowIdx) => {
                                const isBreak = pageBreakBefore.has(rowIdx);
                                if (isBreak) pageNum++;
                                const currentPage = pageNum;
                                const activeSlots = row.cols === 1 ? [0] : [0, 1];

                                return (
                                    <React.Fragment key={row.id}>
                                        {isBreak && (
                                            <div className="no-print" style={S.pageBreakOuter}>
                                                <div style={S.pageBreakShadowTop} />
                                                <div style={S.pageBreakBand}>
                                                    {!quizLocked && (
                                                        <div style={{ display: 'flex', gap: 5 }}>
                                                            <button onClick={() => addRow(rows[rowIdx - 1].id, 1)} style={S.pageBreakAddBtn} title={`Add 1 question to page ${currentPage - 1}`}>+1Q ↑</button>
                                                            <button onClick={() => addRow(rows[rowIdx - 1].id, 2)} style={S.pageBreakAddBtn} title={`Add 2 questions to page ${currentPage - 1}`}>+2Q ↑</button>
                                                        </div>
                                                    )}
                                                    <span style={S.pageBreakBandLabel}>Page {currentPage}</span>
                                                    {!quizLocked && (
                                                        <div style={{ display: 'flex', gap: 5 }}>
                                                            <button onClick={() => addRowBefore(row.id, 1)} style={S.pageBreakAddBtn} title={`Add 1 question to page ${currentPage}`}>↓ +1Q</button>
                                                            <button onClick={() => addRowBefore(row.id, 2)} style={S.pageBreakAddBtn} title={`Add 2 questions to page ${currentPage}`}>↓ +2Q</button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={S.pageBreakShadowBottom} />
                                            </div>
                                        )}

                                        <div
                                            ref={el => { if (el) rowDivRefs.current[row.id] = el; else delete rowDivRefs.current[row.id]; }}
                                            className="ws-row"
                                            style={{ ...S.row, ...(isBreak ? { pageBreakBefore: 'always', breakBefore: 'page' } : {}) }}
                                        >
                                            {activeSlots.map(si => {
                                                const qid = row.slots[si];
                                                const q = qid ? questionMap[qid] : null;
                                                const isTarget = dragOverTarget?.type === 'slot'
                                                    && dragOverTarget.rowId === row.id
                                                    && dragOverTarget.slotIdx === si;
                                                const qNum = qid ? getQNum(rowIdx, si) : null;

                                                return (
                                                    <div
                                                        key={si}
                                                        className="ws-slot"
                                                        style={{
                                                            ...S.slot,
                                                            ...(isTarget ? S.slotOver : {}),
                                                            ...(q ? S.slotFilled : S.slotEmpty),
                                                        }}
                                                        onDragOver={quizLocked ? undefined : e => onDragOver(e, { type: 'slot', rowId: row.id, slotIdx: si })}
                                                        onDragLeave={quizLocked ? undefined : onDragLeave}
                                                        onDrop={quizLocked ? undefined : e => onDrop(e, { type: 'slot', rowId: row.id, slotIdx: si })}
                                                    >
                                                        {q ? (
                                                            <CanvasQuestion
                                                                q={q}
                                                                qid={qid}
                                                                qNum={qNum}
                                                                rowId={row.id}
                                                                slotIdx={si}
                                                                rowCols={row.cols}
                                                                fontSize={config.fontSize}
                                                                settings={questionSettings[qid] || {}}
                                                                onSettingChange={(key, val) => setQSetting(qid, key, val)}
                                                                onRequestSingleRow={quizLocked ? null : () => toggleRowCols(row.id)}
                                                                onDragStart={quizLocked ? null : onDragStart}
                                                                onDragEnd={quizLocked ? null : onDragEnd}
                                                                onClear={quizLocked ? null : () => clearSlot(row.id, si)}
                                                                answerKey={answerKey}
                                                                katexReady={katexReady}
                                                                locked={quizLocked}
                                                            />
                                                        ) : (
                                                            <EmptySlot slotIdx={si} isSingle={row.cols === 1} />
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Row controls */}
                                            {!quizLocked && (
                                                <div className="no-print" style={S.rowControls}>
                                                    <button
                                                        onClick={() => toggleRowCols(row.id)}
                                                        style={{ ...S.rowCtrlBtn, fontSize: 10, width: 28, height: 20, borderRadius: 5 }}
                                                        title={row.cols === 2 ? 'Switch to 1-column' : 'Switch to 2-column'}
                                                    >
                                                        {row.cols === 2 ? '1Q' : '2Q'}
                                                    </button>
                                                    <button onClick={() => addRow(row.id, row.cols)} style={S.rowCtrlBtn} title="Add same-type row below">+</button>
                                                    <button onClick={() => removeRow(row.id)} style={{ ...S.rowCtrlBtn, ...S.rowCtrlDanger }} title="Remove row">×</button>
                                                </div>
                                            )}
                                        </div>

                                    </React.Fragment>
                                );
                            });
                            })()}
                            {/* Add row at end of last page */}
                            {!quizLocked && (
                                <div className="no-print" style={S.pageAddRowBar}>
                                    <button onClick={() => addRow(rows[rows.length - 1]?.id, 1)} style={{ ...S.addRowBtn, flex: 1 }}>
                                        + 1 Question
                                    </button>
                                    <button onClick={() => addRow(rows[rows.length - 1]?.id, 2)} style={{ ...S.addRowBtn, flex: 1 }}>
                                        + 2 Questions
                                    </button>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                </main>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function DocHeader({ config }) {
    const fields = [
        config.showName && 'Name',
        config.showDate && 'Date',
        config.showScore && 'Score',
    ].filter(Boolean);

    return (
        <div style={S.docHeader}>
            {/* Top accent bar */}
            <div style={{ height: 4, background: 'linear-gradient(90deg,#1d4ed8,#4f46e5,#7c3aed)', borderRadius: 2, marginBottom: 14 }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                    {config.subtitle && (
                        <p style={S.docSubtitle}>{config.subtitle}</p>
                    )}
                    <h1 style={S.docTitle}>{config.title || 'Worksheet'}</h1>
                </div>
                <div style={{ textAlign: 'right', fontSize: '10pt', color: '#555', lineHeight: 2, paddingTop: 2 }}>
                    {config.teacher && <p style={{ margin: 0 }}>Teacher: <strong>{config.teacher}</strong></p>}
                    {config.date && <p style={{ margin: 0, color: '#777' }}>{config.date}</p>}
                </div>
            </div>

            {fields.length > 0 && (
                <div style={{ ...S.studentRow, borderTop: '1px solid #e0e0e0', paddingTop: 10 }}>
                    {fields.map(f => (
                        <div key={f} style={S.studentField}>
                            <span style={{ color: '#333', fontWeight: 600, marginRight: 6, fontFamily: 'sans-serif', fontSize: '9.5pt' }}>{f}:</span>
                            <span style={{ display: 'inline-block', borderBottom: '1.5px solid #555', flex: 1, minWidth: 100 }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PoolCard({ qid, idx, q, isUsed, onDragStart, onDragEnd }) {
    const skill = q?.skill || q?.topic || '';
    const diff = q?.difficulty || '';
    const preview = q ? stripHtml(q.stem) : `Question ID: ${qid}`;

    return (
        <div
            draggable={!isUsed}
            onDragStart={e => !isUsed && onDragStart(e, { source: 'pool', qid })}
            onDragEnd={onDragEnd}
            style={{ ...S.poolCard, opacity: isUsed ? 0.38 : 1, cursor: isUsed ? 'default' : 'grab' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={S.poolQnum}>{idx + 1}</span>
                {skill && <span style={S.poolTag}>{skill.slice(0, 32)}</span>}
                {diff && (
                    <span style={{
                        ...S.poolTag,
                        background: diff === 'Hard' ? '#fee2e2' : diff === 'Easy' ? '#dcfce7' : '#fef9c3',
                        color: diff === 'Hard' ? '#dc2626' : diff === 'Easy' ? '#16a34a' : '#ca8a04',
                        border: 'none'
                    }}>{diff}</span>
                )}
                {isUsed && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#059669', fontWeight: 600 }}>✓ placed</span>}
            </div>
            <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, margin: 0 }}>
                {preview}
            </p>
        </div>
    );
}

const MIN_FONT = 7;

function CanvasQuestion({ q, qid, qNum, rowId, slotIdx, rowCols = 2, fontSize = 11, settings, onSettingChange, onRequestSingleRow, onDragStart, onDragEnd, onClear, answerKey = 'off', katexReady = false, locked = false }) {
    const contentRef = useRef(null);
    const [showEdit, setShowEdit] = useState(false);
    const [effectiveFontSize, setEffectiveFontSize] = useState(fontSize);
    const skill = q?.skill || q?.topic || '';
    const diff = q?.difficulty || '';

    // Reset effective font when prop changes
    useEffect(() => { setEffectiveFontSize(fontSize); }, [fontSize, q]);

    useEffect(() => {
        if (!contentRef.current) return;

        // KaTeX rendering — must run every time so math is re-rendered after
        // dangerouslySetInnerHTML resets the DOM on re-renders
        if (window.renderMathInElement) {
            try {
                window.renderMathInElement(contentRef.current, {
                    delimiters: [
                        { left: '\\[', right: '\\]', display: true  },
                        { left: '$$',  right: '$$',  display: true  },
                        { left: '\\(', right: '\\)', display: false },
                        { left: '$',   right: '$',   display: false },
                    ],
                    throwOnError: false,
                });
            } catch {}
        }

        const checkOverflow = () => {
            if (!contentRef.current) return false;
            const el = contentRef.current;
            const w = el.offsetWidth;
            if (!w) return false;
            if (el.scrollWidth > w + 5) return true;
            const candidates = el.querySelectorAll('.katex-display, table, img, svg');
            for (const c of candidates) {
                if (c.scrollWidth > w + 5 || c.offsetWidth > w - 2) return true;
            }
            return false;
        };

        const runChecks = () => {
            if (!contentRef.current) return;

            // Step 1: if 2Q and overflows → promote to 1Q first
            if (rowCols === 2 && onRequestSingleRow && checkOverflow()) {
                onRequestSingleRow();
                return;
            }

            // Step 2: if 1Q and still overflows → shrink font
            if (rowCols === 1 && checkOverflow()) {
                setEffectiveFontSize(prev => {
                    if (prev <= MIN_FONT) return prev;
                    return parseFloat((prev - 0.5).toFixed(1));
                });
            }
        };

        // Double RAF + two passes for KaTeX / table layout to settle
        requestAnimationFrame(() => requestAnimationFrame(() => {
            runChecks();
            setTimeout(runChecks, 100);
        }));
    }, [q, rowCols, effectiveFontSize, katexReady]);

    const hasChoices = q?.a != null && q.a !== 'null';
    const choices = hasChoices
        ? [['A', q.a], ['B', q.b], ['C', q.c], ['D', q.d]].filter(([, v]) => v != null && v !== 'null')
        : [];

    // Determine grid layout: user setting > auto-detect
    const choiceLayout = settings.choiceLayout || 'auto';
    const autoSingleCol = choices.some(([, v]) => stripHtml(v).length > 35);
    const useSingleCol = choiceLayout === '1col' || (choiceLayout === 'auto' && autoSingleCol);

    return (
        <div
            draggable={!locked}
            onDragStart={locked ? undefined : e => onDragStart(e, { source: 'canvas', qid, rowId, slotIdx })}
            onDragEnd={locked ? undefined : onDragEnd}
            style={S.canvasQ}
        >
            {/* Screen-only controls bar (hidden on print) */}
            <div className="no-print" style={S.canvasQHeader}>
                {skill && <span style={S.canvasQSkill}>{skill.slice(0, 32)}</span>}
                {diff && (
                    <span style={{
                        ...S.canvasQSkill,
                        background: diff === 'Hard' ? '#fee2e2' : diff === 'Easy' ? '#dcfce7' : '#fef9c3',
                        color: diff === 'Hard' ? '#b91c1c' : diff === 'Easy' ? '#15803d' : '#a16207',
                        borderColor: diff === 'Hard' ? '#fecaca' : diff === 'Easy' ? '#bbf7d0' : '#fde68a',
                    }}>{diff}</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={S.qidBadge} title="Question ID">{qid}</span>
                    {effectiveFontSize < fontSize && (
                        <span style={{ fontSize: 9, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 3, padding: '1px 5px', fontFamily: 'sans-serif' }}
                            title={`Auto-shrunk from ${fontSize}pt`}>
                            {effectiveFontSize}pt
                        </span>
                    )}
                    {!locked && (
                        <>
                            <button onClick={() => setShowEdit(v => !v)} style={{ ...S.editBtn, ...(showEdit ? S.editBtnActive : {}) }} title="Question settings">
                                ✎ Edit
                            </button>
                            <button onClick={onClear} style={S.clearBtn} title="Remove">×</button>
                        </>
                    )}
                </div>
            </div>

            {/* Inline edit panel (screen only) */}
            {showEdit && (
                <div className="no-print" style={S.editPanel}>
                    <span style={S.editPanelLabel}>Choices layout</span>
                    <div style={{ display: 'flex', gap: 5 }}>
                        {[['auto', 'Auto'], ['1col', '1 Column'], ['2x2', '2 × 2']].map(([val, label]) => (
                            <button key={val} onClick={() => onSettingChange('choiceLayout', val)}
                                style={{ ...S.layoutBtn, ...(choiceLayout === val ? S.layoutBtnActive : {}) }}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Question body — number inline with stem */}
            <div ref={contentRef} className="ws-q-body" style={{ ...S.qBody, fontSize: `${effectiveFontSize}pt` }}>

                {/* Stimulus (passage) — above the numbered question */}
                {q?.stimulus && (
                    <div className="ws-stimulus" style={{ ...S.stimulusBox, fontSize: `${(effectiveFontSize * 0.9).toFixed(1)}pt` }}
                        dangerouslySetInnerHTML={{ __html: q.stimulus }} />
                )}

                {/* Number + stem + choices on same flow */}
                <div style={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
                    {/* Question number */}
                    <span style={{
                        fontWeight: 900,
                        flexShrink: 0,
                        fontSize: `${effectiveFontSize}pt`,
                        lineHeight: 1.55,
                        color: '#111',
                        fontFamily: "'Times New Roman', Times, serif",
                        minWidth: `${effectiveFontSize * 1.6}px`,
                    }}>
                        {qNum}.
                    </span>

                    {/* Stem + choices */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {q?.stem && (
                            <div className="ws-stem" style={{ ...S.stemBox, fontSize: `${effectiveFontSize}pt`, marginBottom: 6 }}
                                dangerouslySetInnerHTML={{ __html: q.stem }} />
                        )}
                        {choices.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: useSingleCol ? '1fr' : '1fr 1fr',
                                gap: '4px 12px',
                                marginTop: 4,
                            }}>
                                {choices.map(([letter, text]) => {
                                    let correctLetter = '';
                                    if (answerKey === 'inline' && q?.correct_answer) {
                                        try {
                                            const parsed = JSON.parse(q.correct_answer);
                                            correctLetter = (Array.isArray(parsed) ? parsed[0] : String(parsed)).trim().toUpperCase();
                                        } catch {
                                            correctLetter = q.correct_answer.toString().trim().toUpperCase();
                                        }
                                    }
                                    const isCorrect = answerKey === 'inline' && correctLetter === letter;
                                    return (
                                        <div key={letter} className="ws-choice" style={{
                                            ...S.choiceRow,
                                            fontSize: `${(effectiveFontSize * 0.95).toFixed(1)}pt`,
                                            ...(isCorrect ? {
                                                background: '#dcfce7',
                                                borderRadius: 4,
                                                outline: '1.5px solid #16a34a',
                                                padding: '2px 4px',
                                            } : {}),
                                        }}>
                                            <span style={{
                                                ...S.choiceLetter,
                                                ...(isCorrect ? { color: '#15803d', fontWeight: 900 } : {}),
                                            }}>{letter}.</span>
                                            <span dangerouslySetInnerHTML={{ __html: text }} />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Free response: show blank line, or parsed answer in answer key mode */
                            answerKey === 'inline' && q?.correct_answer ? (() => {
                                let displayAns = q.correct_answer;
                                try {
                                    const parsed = JSON.parse(q.correct_answer);
                                    displayAns = Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
                                } catch {}
                                return (
                                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: `${(effectiveFontSize * 0.9).toFixed(1)}pt`, color: '#6b7280' }}>Ans:</span>
                                        <span style={{
                                            fontSize: `${effectiveFontSize}pt`,
                                            fontWeight: 700,
                                            color: '#15803d',
                                            borderBottom: '1.5px solid #16a34a',
                                            minWidth: 60,
                                            paddingBottom: 2,
                                        }}>{displayAns}</span>
                                    </div>
                                );
                            })() : (
                                <div style={{ borderBottom: '1px solid #bbb', marginTop: 8, height: 20 }} />
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function RationaleSheet({ rows, questionMap, config, getQNum, katexReady }) {
    const contentRef = useRef(null);

    const items = [];
    rows.forEach((row, rowIdx) => {
        const activeSlots = row.cols === 1 ? [0] : [0, 1];
        activeSlots.forEach(si => {
            const qid = row.slots[si];
            if (!qid) return;
            const q = questionMap[qid];
            if (!q) return;
            items.push({ qid, q, num: getQNum(rowIdx, si) });
        });
    });

    useEffect(() => {
        if (!contentRef.current || !window.renderMathInElement) return;
        try {
            window.renderMathInElement(contentRef.current, {
                delimiters: [
                    { left: '\\[', right: '\\]', display: true },
                    { left: '$$', right: '$$', display: true },
                    { left: '\\(', right: '\\)', display: false },
                    { left: '$', right: '$', display: false },
                ],
                throwOnError: false,
            });
        } catch {}
    }, [items.length, katexReady]);

    return (
        <div
            className="a4-outer rationale-sheet"
            ref={contentRef}
            style={{ ...S.a4Outer, minHeight: 'unset' }}
        >
            {/* Header */}
            <div style={{ borderBottom: '2px solid #222', paddingBottom: 14, marginBottom: 24 }}>
                <div style={{ height: 4, background: 'linear-gradient(90deg,#5b21b6,#7c3aed,#4f46e5)', borderRadius: 2, marginBottom: 14 }} />
                <h1 style={{ fontSize: '16pt', margin: 0, fontFamily: 'Georgia, serif', fontWeight: 800, color: '#111' }}>
                    {config.title || 'Worksheet'} — Answer Key &amp; Explanations
                </h1>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {items.length === 0 && (
                    <p style={{ color: '#9ca3af', fontStyle: 'italic', fontFamily: 'sans-serif', fontSize: '10pt' }}>
                        No questions placed on the worksheet yet.
                    </p>
                )}
                {items.map(({ qid, q, num }) => {
                    let correctAnswer = '';
                    if (q?.correct_answer) {
                        try {
                            const parsed = JSON.parse(q.correct_answer);
                            const raw = Array.isArray(parsed) ? parsed[0] : parsed;
                            correctAnswer = String(raw ?? '').trim().toUpperCase();
                        } catch {
                            correctAnswer = q.correct_answer.toString().trim().toUpperCase();
                        }
                    }

                    return (
                        <div key={qid} style={{ pageBreakInside: 'avoid', breakInside: 'avoid', borderBottom: '1px solid #e5e7eb', paddingBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <span style={{ fontWeight: 900, fontSize: '11pt', color: '#111', fontFamily: "'Times New Roman', Times, serif", flexShrink: 0, minWidth: 28 }}>
                                    {num}.
                                </span>
                                {correctAnswer && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#dcfce7', border: '1.5px solid #16a34a', borderRadius: 6, padding: '2px 12px', fontSize: '10pt', fontWeight: 700, color: '#15803d', fontFamily: 'sans-serif' }}>
                                        Answer: {correctAnswer}
                                    </span>
                                )}
                            </div>
                            {q?.rationale ? (
                                <div
                                    className="ws-q-body"
                                    style={{ fontSize: '10pt', color: '#333', lineHeight: 1.65, fontFamily: "'Times New Roman', Times, serif", paddingLeft: 28 }}
                                    dangerouslySetInnerHTML={{ __html: q.rationale }}
                                />
                            ) : (
                                <p style={{ fontSize: '10pt', color: '#9ca3af', fontStyle: 'italic', margin: '0 0 0 28px', fontFamily: 'sans-serif' }}>
                                    No explanation available.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function EmptySlot({ slotIdx, isSingle }) {
    return (
        <div style={S.emptySlotInner}>
            <span style={{ fontSize: 22, marginBottom: 4, opacity: 0.25 }}>⊕</span>
            <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.25)' }}>
                {isSingle ? 'Drop question here (full width)' : slotIdx === 0 ? 'Drop question here' : 'Or drop for 2-column'}
            </span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small UI helpers
// ─────────────────────────────────────────────────────────────────────────────

function Section({ label, children }) {
    return (
        <div style={S.section}>
            <p style={{ ...S.microLabel, marginBottom: 14 }}>{label}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {children}
            </div>
        </div>
    );
}

function LabeledInput({ label, value, onChange, placeholder = '' }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, width: 64, flexShrink: 0 }}>{label}</span>
            <input
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                style={S.input}
            />
        </div>
    );
}

function ToggleChip({ active, onClick, label }) {
    return (
        <button onClick={onClick} style={{ ...S.chip, ...(active ? S.chipActive : {}) }}>
            {active ? '✓ ' : ''}{label}
        </button>
    );
}

function SubCategoryPanel({ category, skills, selectedSkills, loading, onClose, onToggleSkill, onSelectAll, onClearAll }) {
    const [search, setSearch] = React.useState('');
    const filtered = skills.filter(s => s.toLowerCase().includes(search.toLowerCase()));
    const allSelected = selectedSkills.length === 0 || skills.every(s => selectedSkills.includes(s));

    return (
        <div className="no-print" style={S.subPanel}>
            {/* Header */}
            <div style={S.subPanelHead}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 2px', fontFamily: 'sans-serif' }}>Sub-Category</p>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'white', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{category}</p>
                </div>
                <button onClick={onClose} style={S.subPanelClose}>✕</button>
            </div>

            {/* Search bar */}
            <div style={{ padding: '10px 14px 6px', flexShrink: 0 }}>
                <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }}>🔍</span>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search skills…"
                        style={{ ...S.input, paddingLeft: 30, width: '100%', boxSizing: 'border-box', fontSize: 13 }}
                    />
                </div>
            </div>

            {/* Select All / Clear */}
            <div style={{ padding: '4px 14px 8px', display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={onSelectAll} style={S.subPanelActionBtn}>All</button>
                <button onClick={onClearAll} style={S.subPanelActionBtn}>None</button>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>
                    {selectedSkills.length === 0 ? skills.length : skills.filter(s => selectedSkills.includes(s)).length}/{skills.length}
                </span>
            </div>

            {/* Skill list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {loading && <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading…</p>}
                {!loading && filtered.length === 0 && (
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No results</p>
                )}
                {!loading && filtered.map(skill => {
                    const checked = selectedSkills.length === 0 || selectedSkills.includes(skill);
                    return (
                        <label key={skill} style={{ ...S.filterRow, padding: '6px 8px', borderRadius: 7, background: checked ? 'rgba(99,102,241,0.1)' : 'transparent', cursor: 'pointer' }}>
                            <span style={{
                                ...S.filterCheck,
                                background: checked ? '#4f46e5' : 'rgba(255,255,255,0.06)',
                                borderColor: checked ? '#6366f1' : 'rgba(255,255,255,0.18)',
                            }}>
                                {checked && <span style={{ fontSize: 9, color: 'white', lineHeight: 1 }}>✓</span>}
                            </span>
                            <span style={{ fontSize: 13, color: checked ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)', lineHeight: 1.35, flex: 1 }}>{skill}</span>
                            <input type="checkbox" checked={checked} onChange={() => onToggleSkill(skill)} style={{ display: 'none' }} />
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

function FilterAccordion({ label, open, onToggle, children }) {
    return (
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <button onClick={onToggle} style={S.accordionHead}>
                <span style={S.accordionLabel}>{label}</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', transition: 'transform 0.18s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
            </button>
            {open && <div style={S.accordionBody}>{children}</div>}
        </div>
    );
}

function FilterCheckRow({ label, checked, onChange, radio = false, dot }) {
    return (
        <label style={S.filterRow}>
            <span style={{
                ...S.filterCheck,
                background: checked ? '#4f46e5' : 'rgba(255,255,255,0.06)',
                borderColor: checked ? '#6366f1' : 'rgba(255,255,255,0.18)',
            }}>
                {checked && (radio
                    ? <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', display: 'block', margin: 'auto' }} />
                    : <span style={{ fontSize: 9, color: 'white', lineHeight: 1 }}>✓</span>
                )}
            </span>
            {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
            <span style={{ fontSize: 13, color: checked ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)', lineHeight: 1.3, flex: 1 }}>{label}</span>
            <input type="checkbox" checked={checked} onChange={onChange} style={{ display: 'none' }} />
        </label>
    );
}

function Spinner() {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0e1a', color: 'rgba(255,255,255,0.45)', fontFamily: 'sans-serif', gap: 12, flexDirection: 'column' }}>
            <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Loading worksheet…
        </div>
    );
}

function PrintStyles({ fontSize = 11 }) {
    const fs = fontSize;
    return (
        <style>{`
            /* Dropdown options — always dark text on white background */
            aside select option {
                color: #111 !important;
                background: #fff !important;
            }

            @media print {
                .no-print, aside { display: none !important; }

                /* Use 100% width — let @page handle the paper size.
                   Setting px/mm widths on html/body causes the browser to
                   calculate a fit-to-page scale (the 30% shrink). */
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    background: white !important;
                }
                .ws-root  {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                    overflow: visible !important;
                    background: white !important;
                }
                .ws-right {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                    overflow: visible !important;
                }
                .print-canvas-scroll {
                    display: block !important;
                    width: 100% !important;
                    overflow: visible !important;
                    height: auto !important;
                    background: white !important;
                    padding: 0 !important;
                }
                .a4-outer {
                    width: 100% !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    min-height: unset !important;
                }
                .rationale-sheet {
                    page-break-before: always !important;
                    break-before: page !important;
                    padding: 0 !important;
                }

                /* Tighten spacing between rows */
                .ws-row {
                    margin-bottom: 10px !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }
                .ws-slot {
                    border: none !important;
                    background: transparent !important;
                    border-radius: 0 !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }

                /* Tighten question card padding */
                .ws-q-card {
                    padding: 4px 0 6px 0 !important;
                }

                /* Font sizes — user setting, compact line-height */
                .ws-q-body   { font-size: ${fs}pt !important; line-height: 1.55 !important; }
                .ws-stimulus { font-size: ${(fs * 0.9).toFixed(1)}pt !important; line-height: 1.5 !important; border-left: 3px solid #888 !important; background: #f5f5f5 !important; padding: 4px 7px !important; margin-bottom: 5px !important; }
                .ws-stem     { font-size: ${fs}pt !important; line-height: 1.55 !important; margin-bottom: 4px !important; }
                .ws-choice   { font-size: ${(fs * 0.95).toFixed(1)}pt !important; line-height: 1.45 !important; margin-bottom: 1px !important; }
            }
            @page { size: A4; margin: 14mm 14mm 10mm 14mm; }

            /* ── Always (screen + print) ── */

            /* KaTeX default is 1.21em — override to match surrounding text size.
               vertical-align corrects the baseline shift caused by the size change. */
            .ws-q-body .katex,
            .ws-stimulus .katex,
            .ws-stem .katex {
                font-size: 1em !important;
                vertical-align: -2px !important;
            }

            /* Images: center and cap width */
            .ws-q-body img,
            .ws-stimulus img,
            .ws-stem img {
                display: block !important;
                margin: 8px auto !important;
                max-width: 100% !important;
                height: auto !important;
            }

            /* Prevent KaTeX from adding scrollbars (its default is overflow:hidden auto).
               Tables, stimulus, and stem stay visible so content isn't clipped. */
            .ws-q-body .katex-display,
            .ws-q-body .katex-html,
            .ws-stimulus .katex-display,
            .ws-stem .katex-display {
                overflow: visible !important;
            }
            .ws-q-body,
            .ws-stimulus,
            .ws-stem {
                overflow: visible !important;
            }
            /* KaTeX display centering (keep after overflow reset) */
            .ws-q-body .katex-display,
            .ws-stimulus .katex-display,
            .ws-stem .katex-display {
                display: block !important;
                text-align: center !important;
                margin: 8px 0 !important;
            }
            /* KaTeX inline that wraps a full line — also center */
            .ws-q-body .katex-display > .katex,
            .ws-stem .katex-display > .katex {
                display: inline-block !important;
            }

            /* SVG / figures (Desmos-style or inline SVG graphs) */
            .ws-q-body svg,
            .ws-stem svg,
            .ws-stimulus svg {
                display: block !important;
                margin: 8px auto !important;
                max-width: 100% !important;
            }

            /* Tables */
            .ws-stimulus table, .ws-stem table {
                border-collapse: collapse;
                font-size: 9.5pt;
                margin: 6px auto;
            }
            .ws-stimulus td, .ws-stem td,
            .ws-stimulus th, .ws-stem th { border: 1px solid #ccc; padding: 3px 6px; }

            /* Paragraphs */
            .ws-stimulus p, .ws-stem p { margin: 0 0 4px 0; }
        `}</style>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const S = {
    root: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#0a0e1a', fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif' },

    // Left — wide panel
    left: { width: 520, minWidth: 520, background: '#111827', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.22s ease, min-width 0.22s ease' },
    leftTopBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 },
    backBtn: { height: 32, padding: '0 12px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0, marginRight: 'auto' },
    collapseBtn: { height: 32, padding: '0 12px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 },
    printBtn: { height: 32, padding: '0 14px', borderRadius: 7, background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', border: '1px solid transparent', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(29,78,216,0.35)' },
    openPanelBtn: { position: 'absolute', top: 14, left: 14, zIndex: 10, width: 38, height: 38, borderRadius: 9, border: '1px solid rgba(255,255,255,0.12)', background: '#1e293b', color: 'rgba(255,255,255,0.7)', fontSize: 18, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' },

    section: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', flexShrink: 0 },
    microLabel: { color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 700, margin: 0 },

    input: { flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, padding: '8px 11px', color: 'white', fontSize: 14, outline: 'none' },
    select: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, padding: '8px 11px', color: 'rgba(255,255,255,0.75)', fontSize: 14, outline: 'none' },
    option: { color: '#111', background: '#fff' },
    chip: { padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)', fontSize: 13, cursor: 'pointer' },
    chipActive: { background: 'rgba(37,99,235,0.22)', border: '1px solid rgba(96,165,250,0.35)', color: '#93c5fd' },

    srcBtn: { flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)', fontSize: 14, cursor: 'pointer' },
    srcBtnActive: { background: 'rgba(37,99,235,0.22)', border: '1px solid rgba(96,165,250,0.35)', color: '#93c5fd', fontWeight: 700 },
    ghostBtn: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer' },
    randomBtn: { width: '100%', padding: '12px', borderRadius: 10, border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', fontSize: 15, fontWeight: 700, cursor: 'pointer' },

    poolDropZone: { flex: 1, borderRadius: 12, border: 'none', background: 'transparent', padding: '14px', minHeight: 100 },
    poolDropZoneOver: { border: '1px solid #6366f1', background: '#eef2ff' },
    poolCard: { background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 13px', transition: 'box-shadow 0.1s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
    poolQnum: { width: 26, height: 26, borderRadius: '50%', background: '#4f46e5', border: 'none', color: 'white', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    poolTag: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', fontSize: 11, color: '#475569' },

    // Right / Canvas
    right: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' },
    canvasScroll: { flex: 1, overflowY: 'auto', overflowX: 'hidden', background: '#1a2035', padding: '36px 64px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' },

    a4Outer: { width: A4_W, minHeight: A4_H, background: 'white', boxShadow: '0 8px 48px rgba(0,0,0,0.55)', padding: `${PAGE_PAD_V}px ${PAGE_PAD_H}px`, color: '#111', fontFamily: "'Times New Roman', Times, serif", position: 'relative' },

    // Doc header
    docHeader: { borderBottom: '2px solid #222', paddingBottom: 14, marginBottom: 20 },
    docTitle: { fontSize: '19pt', margin: '0 0 2px 0', fontFamily: 'Georgia, serif', fontWeight: 800, color: '#111' },
    docSubtitle: { fontSize: '10pt', color: '#666', margin: '0 0 4px 0', letterSpacing: '0.03em' },
    studentRow: { display: 'flex', gap: 32, marginTop: 10 },
    studentField: { display: 'flex', alignItems: 'baseline', gap: 4, fontSize: '10.5pt', flex: 1 },

    // Page break — Google Docs style
    pageBreakOuter: { margin: `8px -${PAGE_PAD_H}px`, display: 'flex', flexDirection: 'column' },
    pageBreakShadowTop: { height: 10, background: 'linear-gradient(to bottom, rgba(0,0,0,0.18), transparent)' },
    pageBreakBand: { background: '#c8cdd6', borderTop: '1px solid #9aa3b0', borderBottom: '1px solid #9aa3b0', padding: '6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    pageBreakBandLabel: { fontSize: 11, fontWeight: 600, color: '#4a5568', fontFamily: 'sans-serif', letterSpacing: '0.06em' },
    pageBreakAddBtn: { padding: '3px 9px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.18)', background: 'rgba(255,255,255,0.65)', color: '#2d3748', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', lineHeight: 1.4 },
    pageBreakShadowBottom: { height: 10, background: 'linear-gradient(to top, rgba(0,0,0,0.18), transparent)' },

    // Row & slots
    row: { display: 'flex', gap: 14, marginBottom: ROW_GAP, position: 'relative', alignItems: 'stretch', pageBreakInside: 'avoid', breakInside: 'avoid' },
    rowControls: { position: 'absolute', right: -60, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' },
    rowCtrlBtn: { width: 24, height: 24, borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
    rowCtrlDanger: { border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.1)', color: 'rgba(239,68,68,0.7)' },

    slot: { flex: 1, borderRadius: 8, minHeight: 120, display: 'flex', flexDirection: 'column', transition: 'all 0.15s', pageBreakInside: 'avoid', breakInside: 'avoid' },
    slotEmpty: { border: '1.5px dashed rgba(0,0,0,0.15)', background: 'rgba(0,0,0,0.015)' },
    slotFilled: { border: '1px solid rgba(0,0,0,0.09)', background: 'white' },
    slotOver: { border: '1.5px dashed #6366f1', background: 'rgba(99,102,241,0.06)' },

    emptySlotInner: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(0,0,0,0.2)', padding: '20px 10px' },

    // Canvas question — full rendering
    canvasQ: { padding: '10px 12px', cursor: 'grab', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
    canvasQHeader: { display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6, borderBottom: '1px solid rgba(0,0,0,0.07)', marginBottom: 4 },
    canvasQNum: { width: 22, height: 22, borderRadius: '50%', background: '#1d4ed8', color: 'white', fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    canvasQSkill: { fontSize: 9, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#4338ca', borderRadius: 4, padding: '1px 6px', fontFamily: 'sans-serif' },
    clearBtn: { width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(239,68,68,0.12)', color: 'rgba(239,68,68,0.7)', fontSize: 13, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
    qidBadge: { fontSize: 9, fontFamily: 'monospace', color: 'rgba(0,0,0,0.35)', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 3, padding: '1px 5px', letterSpacing: '0.02em', userSelect: 'all' },
    editBtn: { padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.12)', background: 'rgba(99,102,241,0.07)', color: '#5b5ef4', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'sans-serif' },
    editBtnActive: { background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.35)' },
    editPanel: { background: '#f0f1ff', border: '1px solid #c7d2fe', borderRadius: 7, padding: '7px 10px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
    editPanelLabel: { fontSize: 10, color: '#5b5ef4', fontWeight: 700, fontFamily: 'sans-serif', whiteSpace: 'nowrap' },
    layoutBtn: { padding: '3px 9px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.12)', background: 'white', color: '#444', fontSize: 10, cursor: 'pointer', fontFamily: 'sans-serif' },
    layoutBtnActive: { background: '#4f46e5', color: 'white', border: '1px solid #4338ca' },

    // Question body
    qBody: { flex: 1, fontSize: '9pt', lineHeight: 1.55, color: '#222', fontFamily: "'Times New Roman', Times, serif" },
    stimulusBox: { fontSize: '8.5pt', color: '#333', background: '#f7f7f7', borderLeft: '3px solid #bbb', padding: '6px 9px', marginBottom: 8, borderRadius: '0 4px 4px 0', lineHeight: 1.5 },
    stemBox: { fontSize: '9.5pt', color: '#111', marginBottom: 4 },
    choiceRow: { display: 'flex', gap: 5, alignItems: 'flex-start', fontSize: '9pt', lineHeight: 1.4 },
    choiceLetter: { fontWeight: 700, minWidth: 14, color: '#333', flexShrink: 0 },

    pageAddRowBar: { display: 'flex', gap: 8, marginTop: 6, marginBottom: 2 },
    addRowBtn: { display: 'block', width: '100%', marginTop: 0, padding: '9px', borderRadius: 9, border: '1.5px dashed rgba(0,0,0,0.18)', background: 'transparent', color: 'rgba(0,0,0,0.35)', fontSize: 13, cursor: 'pointer', fontFamily: 'sans-serif' },
    fontStepBtn: { width: 30, height: 30, borderRadius: 7, border: '1px solid rgba(255,255,255,0.13)', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: 18, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1 },
    lockedSubjectBadge: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, padding: '8px 12px', color: '#a5b4fc' },

    // OSU-style filter accordion
    accordionHead: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: 'none', cursor: 'pointer', userSelect: 'none' },
    accordionLabel: { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'sans-serif' },
    accordionBody: { padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', gap: 4 },
    filterRow: { display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', padding: '4px 2px', borderRadius: 6 },
    filterCheck: { width: 16, height: 16, borderRadius: 4, border: '1.5px solid', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s, border-color 0.15s' },
    subCatBtn: { width: 26, height: 26, borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, transition: 'background 0.15s' },
    loadBtn: { padding: '9px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' },

    // Sub-category floating panel
    subPanel: { width: 300, minWidth: 300, background: '#1a2236', borderRight: '1px solid rgba(255,255,255,0.09)', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderLeft: '1px solid rgba(255,255,255,0.07)' },
    subPanelHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 },
    subPanelClose: { width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.45)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    subPanelActionBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', fontSize: 12, cursor: 'pointer', fontFamily: 'sans-serif' },
};
